import { useState, FormEvent, useCallback, useEffect } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  TextField,
  Typography,
  Link,
  CircularProgress,
} from "@mui/material";
import { useAuth } from "../../hooks";
import { ErrorDisplay } from "../common/ErrorDisplay";

const RequestPasswordResetForm = () => {
  const navigate = useNavigate();
  const {
    resetPasswordRequest,
    error: authError,
    isLoading,
    clearError,
  } = useAuth();
  const [email, setEmail] = useState("");
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  // Validate email
  const validateEmail = useCallback((email: string): boolean => {
    const errors: Record<string, string> = {};

    if (!email) {
      errors.email = "Email is required";
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)) {
      errors.email = "Invalid email address";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, []);

  // Clear error when email changes
  useEffect(() => {
    if (authError && email) {
      clearError();
    }
  }, [email, authError, clearError]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setValidationErrors({});

    if (!validateEmail(email)) {
      return;
    }

    try {
      await resetPasswordRequest(email);
      // resetPasswordRequest rejects on failure, so reaching here means success.
      navigate("/auth/reset-password-sent", { state: { email } });
    } catch {
      // Failure: stay on this page; authError from the provider renders above.
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: "100%" }}>
      <Typography component="h2" variant="h6" align="center" gutterBottom>
        Reset Your Password
      </Typography>

      <Typography variant="body2" align="center" sx={{ mb: 3 }}>
        Enter your email address and we'll send you a link to reset your
        password
      </Typography>

      {authError && <ErrorDisplay error={authError} onClose={clearError} />}

      <TextField
        margin="normal"
        required
        fullWidth
        id="email"
        label="Email Address"
        name="email"
        autoComplete="email"
        autoFocus
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        error={!!validationErrors.email}
        helperText={validationErrors.email}
        disabled={isLoading}
      />

      <Button
        type="submit"
        fullWidth
        variant="contained"
        sx={{ mt: 3, mb: 2 }}
        disabled={isLoading}
      >
        {isLoading ? <CircularProgress size={24} /> : "Send Reset Link"}
      </Button>

      <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
        <Link component={RouterLink} to="/auth/login" variant="body2">
          Back to login
        </Link>
      </Box>
    </Box>
  );
};

export default RequestPasswordResetForm;
